/**
 * #1952 — what makes pasted text unsaveable, and how to say so.
 *
 * ed: *"There are often invalid text errors when cutting and pasting employer
 * requests … the errors aren't highlighted or identified, just error code pops
 * and I cannot save the job."* Then, 2026-09-30: **"a, show it"**.
 *
 * ══ IT WAS NEVER THE BULLETS ══
 *
 * The old `containsInvalidContent()` carried four patterns. Run over realistic
 * pasted text, bullets (`•`) and smart quotes are ACCEPTED; what rejected a
 * request was:
 *
 *     (.)\1{3,}                      any character four times   ← FOUR SPACES of
 *                                    indentation, a tab run, a ---- or ==== rule
 *     [.?!,#$^&*()@%|"':;<>]{3,}     three punctuation marks    ← an ellipsis "..."
 *
 * So the trigger was invisible whitespace, which is exactly why WWE RAW on L18
 * could not be pinpointed by eye. Neither pattern has any security value, and
 * neither is enforced server-side (#2005), so both are gone.
 *
 * ══ WHAT IS LEFT, AND ITS HONEST LIMIT ══
 *
 * The script-signature check stays, at ed's instruction. Be clear about what it
 * does: callers pass text that has ALREADY had its markup stripped, so this
 * matches those strings only when they appear as literal prose — pasting
 * `<script>alert(1)</script>` leaves `alert(1)` and nothing here fires. It is a
 * typo-and-paste guard, NOT an XSS defence, and the real defence is server-side
 * sanitisation under #2005. Nothing in this module should be read as protecting
 * a `v-html` sink.
 *
 * ══ NEVER REWRITE THE USER'S TEXT ══
 *
 * ed chose "show it" over normalising. These functions only ever REPORT. There is
 * deliberately no `clean()` or `normalise()` here: silently altering a pasted
 * employer request would be worse than refusing it, because nobody would know.
 */

/**
 * The rules, in the order their findings are reported.
 *
 * `label` is what the user is told to remove — it names the thing, which is the
 * whole point of #1952.
 */
export const CONTENT_RULES = [
  {
    id: 'script',
    // Kept verbatim from the original so behaviour on prose text is unchanged.
    pattern: /<script|javascript:|onerror=|onclick=/gi,
    label: 'a script signature',
  },
  {
    id: 'shortener',
    pattern: /bit\.ly|tinyurl|goo\.gl/gi,
    label: 'a link shortener',
  },
];

/**
 * Where a character offset falls, as a human counts it: line 1, character 1.
 */
export function positionOf(text, index) {
  const before = String(text).slice(0, index);
  const nl = before.lastIndexOf('\n');

  return { line: before.split('\n').length, column: index - nl };
}

/**
 * Every problem in `text`, each located.
 *
 * @param {string} text  plain text — callers strip markup first, as they always have
 * @returns {Array<{id:string,label:string,match:string,index:number,line:number,column:number,message:string}>}
 */
export function findContentProblems(text) {
  const s = String(text == null ? '' : text);
  const found = [];

  if (s.trim().length === 0) return found;

  for (const rule of CONTENT_RULES) {
    // A fresh regex per pass. Not strictly required TODAY — the loop below runs
    // exec() until it returns null, and that resets lastIndex — so sharing the
    // /g/ literal happens to be safe, and a mutation to share it passed the whole
    // suite. It is cheap insurance against the version of this loop that breaks
    // early or switches to .test(), either of which would leak lastIndex into the
    // next field validated and silently skip its matches.
    const re = new RegExp(rule.pattern.source, rule.pattern.flags);
    let m;
    while ((m = re.exec(s)) !== null) {
      const { line, column } = positionOf(s, m.index);
      found.push({
        id: rule.id,
        label: rule.label,
        match: m[0],
        index: m.index,
        line,
        column,
        message: `Remove the "${m[0]}" at line ${line}, character ${column}`,
      });
      // A zero-length match would spin forever; nothing here can produce one,
      // but a future rule could.
      if (m.index === re.lastIndex) re.lastIndex++;
    }
  }

  return found.sort((a, b) => a.index - b.index);
}

/** True when the text cannot be saved as it stands. */
export function hasContentProblem(text) {
  return findContentProblems(text).length > 0;
}

/**
 * The message shown to the user. Names every offender and where it is, because
 * "Content contains invalid text. Please review and correct." is the bug.
 */
export function describeContentProblems(findings) {
  const list = findings || [];
  if (list.length === 0) return '';
  if (list.length === 1) return list[0].message + '.';

  return 'Remove: '
    + list.map((f) => `"${f.match}" (line ${f.line}, character ${f.column})`).join(', ')
    + '.';
}

/**
 * Markup-stripped text, replacing ten copies of `getPlainText()`.
 *
 * Browser-only, deliberately: it uses the DOM exactly as the original did, so
 * what gets validated does not change. Falls back to a tag strip where there is
 * no document, so a unit test can call it without a DOM.
 */
export function stripHtml(html) {
  const s = String(html == null ? '' : html);

  if (typeof document === 'undefined') {
    return s.replace(/<[^>]*>/g, '');
  }

  const div = document.createElement('div');
  div.innerHTML = s;

  return div.textContent || div.innerText || '';
}

export default { CONTENT_RULES, findContentProblems, hasContentProblem, describeContentProblems, positionOf, stripHtml };
